import { createContext, useContext, useEffect, useState } from 'react'

type Theme = 'light' | 'dark' | 'high-contrast' | 'system'

interface ThemeContextType {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

interface ThemeProviderProps {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

/**
 * ThemeProvider component
 * - Supports light, dark, high-contrast, and system modes
 * - System mode respects browser/OS preference
 * - Persists theme preference to localStorage
 * - Applies theme class to document root
 * - WCAG 2.1 AA compliant high-contrast mode
 */
export const ThemeProvider = ({
  children,
  defaultTheme = 'system',
  storageKey = 'arborlist-theme',
}: ThemeProviderProps) => {
  const [theme, setTheme] = useState<Theme>(() => {
    const storedTheme = localStorage.getItem(storageKey) as Theme
    return storedTheme || defaultTheme
  })

  useEffect(() => {
    const root = window.document.documentElement

    // Remove all theme classes
    root.classList.remove('light', 'dark', 'high-contrast')

    // Determine which theme to apply
    let appliedTheme: 'light' | 'dark' | 'high-contrast' = 'light'

    if (theme === 'system') {
      // Use system preference
      appliedTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    } else {
      appliedTheme = theme
    }

    // Add current theme class
    root.classList.add(appliedTheme)

    // Save to localStorage
    localStorage.setItem(storageKey, theme)
  }, [theme, storageKey])

  // Listen for system theme changes when in system mode
  useEffect(() => {
    if (theme !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = () => {
      const root = window.document.documentElement
      root.classList.remove('light', 'dark', 'high-contrast')
      root.classList.add(mediaQuery.matches ? 'dark' : 'light')
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [theme])

  const value = {
    theme,
    setTheme,
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

/**
 * Custom hook to access theme context
 */
export const useTheme = () => {
  const context = useContext(ThemeContext)

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }

  return context
}
